import type { NeedKind } from "@station-keeping/sim";

/**
 * Pictogrammes pixel du jeu, dessinés en code sur une grille de 12 × 12 (10 × 10 pour les besoins) : un
 * seul trait d'un pixel, les mêmes pour l'interface (SVG) et pour le plan (sprites des besoins).
 */
export type Bitmap = readonly string[];

export const UI_ICONS = {
  build: [
    "............",
    "....####....",
    "....#..#....",
    "....#..#....",
    ".####..####.",
    ".#........#.",
    ".#........#.",
    ".####..####.",
    "....#..#....",
    "....#..#....",
    "....####....",
    "............",
  ],
  failures: [
    ".....##.....",
    ".....##.....",
    "....####....",
    "....#..#....",
    "...##..##...",
    "...#.##.#...",
    "..##.##.##..",
    "..#..##..#..",
    ".##......##.",
    ".#...##...#.",
    "############",
    "............",
  ],
  ships: [
    "............",
    "............",
    "...##.......",
    "...###......",
    ".#######....",
    "##########..",
    "############",
    "##########..",
    ".#######....",
    "...###......",
    "...##.......",
    "............",
  ],
  station: [
    "....####....",
    "..##....##..",
    ".#........#.",
    ".#...##...#.",
    "#...####...#",
    "#..######..#",
    "#..######..#",
    "#...####...#",
    ".#...##...#.",
    ".#........#.",
    "..##....##..",
    "....####....",
  ],
  credits: [
    "............",
    "...######...",
    "..#......#..",
    ".#..####..#.",
    ".#.#......#.",
    ".#.#......#.",
    ".#.#......#.",
    ".#.#......#.",
    ".#..####..#.",
    "..#......#..",
    "...######...",
    "............",
  ],
  reputation: [
    ".....##.....",
    ".....##.....",
    "....####....",
    "############",
    ".##########.",
    "..########..",
    "...######...",
    "..###..###..",
    "..##....##..",
    ".##......##.",
    ".#........#.",
    "............",
  ],
  sun: [
    ".....##.....",
    ".#...##...#.",
    "..#......#..",
    "....####....",
    "...######...",
    "##.######.##",
    "##.######.##",
    "...######...",
    "....####....",
    "..#......#..",
    ".#...##...#.",
    ".....##.....",
  ],
  shadow: [
    "....#####...",
    "..####......",
    ".####.......",
    ".###........",
    "####........",
    "####........",
    "####........",
    "####........",
    ".###........",
    ".####.......",
    "..####......",
    "....#####...",
  ],
  energy: [
    "......###...",
    ".....###....",
    "....###.....",
    "...###......",
    "..########..",
    ".......###..",
    "......###...",
    ".....###....",
    "....###.....",
    "...##.......",
    "..#.........",
    "............",
  ],
  oxygen: [
    ".####.......",
    "#....#......",
    "#....#......",
    "#....#......",
    "#....#......",
    "#....#......",
    ".####..###..",
    "..........#.",
    ".......###..",
    ".......#....",
    ".......####.",
    "............",
  ],
  crew: [
    "....####....",
    "...######...",
    "...######...",
    "...######...",
    "....####....",
    "............",
    "..########..",
    ".##########.",
    ".##########.",
    ".##########.",
    ".##########.",
    "............",
  ],
  teams: [
    ".........##.",
    "........##..",
    ".....#..##.#",
    ".....#######",
    "....#######.",
    "...###......",
    "..###.......",
    ".###........",
    "###.........",
    "##..........",
    "............",
    "............",
  ],
  condition: [
    "............",
    ".###....###.",
    "#####..#####",
    "############",
    "############",
    ".##########.",
    "..########..",
    "...######...",
    "....####....",
    ".....##.....",
    "............",
    "............",
  ],
  close: [
    "............",
    ".##......##.",
    ".###....###.",
    "..###..###..",
    "...######...",
    "....####....",
    "....####....",
    "...######...",
    "..###..###..",
    ".###....###.",
    ".##......##.",
    "............",
  ],
  power: [
    ".....##.....",
    ".....##.....",
    "..#..##..#..",
    ".##..##..##.",
    ".#...##...#.",
    "##........##",
    "##........##",
    "##........##",
    ".##......##.",
    "..###..###..",
    "...######...",
    "............",
  ],
  trash: [
    "....####....",
    "############",
    "............",
    ".##########.",
    ".#.#.##.#.#.",
    ".#.#.##.#.#.",
    ".#.#.##.#.#.",
    ".#.#.##.#.#.",
    ".#.#.##.#.#.",
    ".#.#.##.#.#.",
    ".##########.",
    "............",
  ],
  patch: [
    ".......####.",
    "......######",
    ".....###..##",
    "....###.#.##",
    "...###.#.###",
    "..###.#.###.",
    ".###.#.###..",
    "##..#.###...",
    "##.#.###....",
    "######......",
    ".####.......",
    "............",
  ],
  ring: [
    "############",
    "#..........#",
    "#.########.#",
    "#.#......#.#",
    "#.#.####.#.#",
    "#.#.#..#.#.#",
    "#.#.#..#.#.#",
    "#.#.####.#.#",
    "#.#......#.#",
    "#.########.#",
    "#..........#",
    "############",
  ],
  check: [
    "............",
    "............",
    "..........##",
    ".........###",
    "........###.",
    ".##....###..",
    ".###..###...",
    "..######....",
    "...####.....",
    "....##......",
    "............",
    "............",
  ],
  clock: [
    "....####....",
    "..##....##..",
    ".#....#...#.",
    ".#....#...#.",
    "#.....#....#",
    "#.....####.#",
    "#..........#",
    "#..........#",
    ".#........#.",
    ".#........#.",
    "..##....##..",
    "....####....",
  ],
  dock: [
    "............",
    "##........##",
    "##........##",
    "##..####..##",
    "##.#....#.##",
    "##.#....#.##",
    "##.#....#.##",
    "##..####..##",
    "##........##",
    "############",
    "############",
    "............",
  ],
  exit: [
    "######......",
    "#....#......",
    "#....#...#..",
    "#....#...##.",
    "#....#######",
    "#....#######",
    "#....#...##.",
    "#....#...#..",
    "#....#......",
    "#....#......",
    "######......",
    "............",
  ],
  mail: [
    "............",
    "............",
    "############",
    "##........##",
    "#.#......#.#",
    "#..#....#..#",
    "#...#..#...#",
    "#....##....#",
    "#..........#",
    "############",
    "............",
    "............",
  ],
  replay: [
    "....####....",
    "..##....##..",
    ".#........#.",
    ".#.........#",
    "#.......####",
    "#........##.",
    "#.........#.",
    "#...........",
    ".#..........",
    ".#........#.",
    "..##....##..",
    "....####....",
  ],
  level: [
    ".....##.....",
    "....####....",
    "...##..##...",
    "..##....##..",
    ".##..##..##.",
    "....####....",
    "...##..##...",
    "..##....##..",
    ".##..##..##.",
    "....####....",
    "...##..##...",
    "..##....##..",
  ],
  upgrade: [
    ".....##.....",
    "....####....",
    "...######...",
    "..###..###..",
    ".###....###.",
    "....####....",
    "....#..#....",
    "....#..#....",
    "....#..#....",
    "....#..#....",
    "....####....",
    "............",
  ],
} as const satisfies Record<string, Bitmap>;

export type UiIconName = keyof typeof UI_ICONS;

/** Besoins des vaisseaux : carburant, réparation, repos, commerce (10 × 10). */
export const NEED_ICONS: Record<NeedKind, Bitmap> = {
  fuel: [
    "....##....",
    "....##....",
    "...####...",
    "...####...",
    "..######..",
    ".########.",
    ".###.####.",
    ".##.#####.",
    "..######..",
    "...####...",
  ],
  repair: [
    "......##..",
    ".....##..#",
    ".....#####",
    "....#####.",
    "...###....",
    "..###.....",
    ".###......",
    "###.......",
    "##........",
    "..........",
  ],
  rest: [
    "..#..#....",
    "...#..#...",
    "..#..#....",
    ".########.",
    ".#......##",
    ".#......#.",
    ".#......##",
    "..######..",
    ".########.",
    "..........",
  ],
  trade: [
    "##########",
    "#.#....#.#",
    "#..#..#..#",
    "#...##...#",
    "#...##...#",
    "#..#..#..#",
    "#.#....#.#",
    "##########",
    "..........",
    "..........",
  ],
};

/** Runs horizontaux [x, y, longueur] d'un pictogramme. */
export function bitmapRuns(bitmap: Bitmap): [number, number, number][] {
  const runs: [number, number, number][] = [];
  bitmap.forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      if (row[x] !== "#") {
        x++;
        continue;
      }
      let end = x;
      while (end < row.length && row[end] === "#") end++;
      runs.push([x, y, end - x]);
      x = end;
    }
  });
  return runs;
}
